import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useCallback, useMemo } from "react";

export type SortableRenderFunction<T> = (info: {
  item: T;
  /**
   * 是否正在拖拽
   */
  isDragging: boolean;
  /**
   * 元素索引
   */
  index: number;
  /**
   * 绑定dom实例
   */
  ref: (node: HTMLElement | null) => void;
  /**
   * 主要是拖拽的位移样式，绑定在拖拽时需要位移的元素上
   */
  props?: React.HTMLAttributes<HTMLElement>;
  /**
   * 拖拽相关的响应事件，绑定到需要响应拖拽的元素上
   */
  handleProps?: React.HTMLAttributes<HTMLElement>;
}) => JSX.Element;

type SortableItemProps<T> = {
  id: UniqueIdentifier;
  item: T;
  renderFunction: SortableRenderFunction<T>;
  disabled?: boolean;
};

const SortableItem = <T extends object>({
  id,
  item,
  renderFunction,
  disabled = false,
}: SortableItemProps<T>) => {
  // 拖拽排序hook，返回一些拖拽相关的数据
  const { setNodeRef, listeners, transform, transition, isDragging, index } =
    useSortable({ id, disabled });

  const cssTransform = CSS.Transform.toString(transform);

  const style = useMemo(() => {
    return {
      transform: cssTransform,
      transition,
    };
  }, [cssTransform, transition]);

  return renderFunction({
    item,
    ref: setNodeRef,
    props: {
      style,
    },
    handleProps: listeners,
    isDragging,
    index,
  });
};

type SortableListProps<T> = {
  items: T[];
  /**
   * 唯一标识获取，用于排序
   */
  getItemId?: (item: T) => UniqueIdentifier;
  /**
   * 判断当前item是否禁止拖拽
   */
  isDragDisabled?: (item: T) => boolean;
  /**
   * 渲染函数
   */
  renderItem: SortableRenderFunction<T>;
  onSortStart?: (event: DragStartEvent) => void;
  onSortEnd: (oldIndex: number, newIndex: number, evnet: DragEndEvent) => void;
  onSortMove: (event: DragMoveEvent) => void;
  onSortCancel?: () => void;
  autoScroll?: boolean;
  children?: React.ReactNode | React.ReactNode[];
};

const defaultGetItemId = (item: any) => item.id;

export const SortableList = <T extends object>({
  items,
  getItemId = defaultGetItemId,
  isDragDisabled = () => false,
  renderItem,
  onSortStart,
  onSortEnd,
  onSortMove,
  onSortCancel,
  autoScroll = true,
  children,
}: SortableListProps<T>) => {
  const itemIds = useMemo(() => items.map(getItemId), [items, getItemId]);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const preventDefault = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      // 阻止默认点击事件
      // 解决拖拽时会触发a标签的链接跳转问题
      document.addEventListener("click", preventDefault, {
        capture: true,
      });
      onSortStart && onSortStart(event);
    },
    [onSortStart, preventDefault]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // if (over && active.id !== over.id) {
      const oldIndex = itemIds.indexOf(active.id);
      const newIndex = over ? itemIds.indexOf(over.id) : oldIndex;
      onSortEnd && onSortEnd(oldIndex, newIndex, event);
      // }

      // 恢复默认点击事件
      setTimeout(() => {
        document.removeEventListener("click", preventDefault, {
          capture: true,
        });
      }, 0);
    },
    [itemIds, onSortEnd, preventDefault]
  );

  const handleDragCancel = useCallback(() => {
    onSortCancel && onSortCancel();
    setTimeout(() => {
      document.removeEventListener("click", preventDefault, {
        capture: true,
      });
    }, 0);
  }, [onSortCancel, preventDefault]);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      onSortMove && onSortMove(event);
    },
    [onSortMove]
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        onDragMove={handleDragMove}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        autoScroll={autoScroll}
      >
        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          {children}
          {itemIds.map((id, i) => (
            <SortableItem
              key={i}
              id={id}
              item={items[i]}
              renderFunction={renderItem}
              disabled={isDragDisabled(items[i])}
            />
          ))}
        </SortableContext>
      </DndContext>
    </>
  );
};
